import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { formatDate } from "../lib/constants";
import { ArrowLeft, Share2, MessageCircle, Copy, Check } from "lucide-react";
import { toast } from "sonner";

export default function NewsDetail() {
  const { slug } = useParams();
  const [item, setItem] = useState(null);
  const [error, setError] = useState(null);
  const [related, setRelated] = useState([]);

  useEffect(() => {
    setItem(null); setError(null);
    api.get(`/news/${slug}`)
      .then((r) => setItem(r.data))
      .catch(() => setError("Notícia não encontrada."));
    api.get("/news?limit=4").then((r) => setRelated(r.data)).catch(() => {});
  }, [slug]);

  if (error) {
    return (
      <div className="container-page py-24" data-testid="news-detail-not-found">
        <p className="text-forest-900/70">{error}</p>
        <Link to="/noticias" className="btn-outline mt-6">Voltar para notícias</Link>
      </div>
    );
  }

  if (!item) {
    return <div className="container-page py-24 text-forest-900/60">Carregando...</div>;
  }

  return (
    <article data-testid="news-detail-page">
      <div className="relative h-[60vh] min-h-[360px] overflow-hidden">
        <img src={item.image_url && item.image_url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${item.image_url}` : item.image_url} alt={item.title} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-forest-900 via-forest-900/50 to-transparent" />
        <div className="container-page relative h-full flex flex-col justify-end pb-14 text-cream-100">
          <div className="eyebrow !text-copaiba-300 mb-3">{formatDate(item.created_at)} &middot; {item.category}</div>
          <h1 className="font-display text-4xl md:text-6xl leading-[1.05] max-w-4xl">{item.title}</h1>
        </div>
      </div>

      <div className="container-page py-16 max-w-3xl">
        <Link to="/noticias" data-testid="back-to-news-link" className="inline-flex items-center gap-2 text-sm text-forest-700 hover:text-copaiba-700 mb-8">
          <ArrowLeft className="w-4 h-4" /> Todas as notícias
        </Link>
        <div className="prose prose-lg max-w-none text-forest-900/90 leading-relaxed whitespace-pre-wrap">
          {item.content}
        </div>

        <ShareBar item={item} />

        {Array.isArray(item.gallery) && item.gallery.length > 0 && (
          <section className="mt-12" data-testid="news-gallery">
            <div className="eyebrow mb-3">Galeria</div>
            <h2 className="font-display text-3xl text-forest-900 mb-6">Imagens e vídeos</h2>
            <div className="grid sm:grid-cols-2 gap-6">
              {item.gallery.map((m, i) => (
                <NewsMedia key={i} item={m} idx={i} />
              ))}
            </div>
          </section>
        )}
      </div>

      {related.filter((r) => r.slug !== item.slug).length > 0 && (
        <section className="bg-cream-200 py-20">
          <div className="container-page">
            <div className="eyebrow mb-3">Continue lendo</div>
            <h2 className="font-display text-3xl md:text-4xl text-forest-900 mb-10">Outras notícias</h2>
            <div className="grid md:grid-cols-3 gap-8">
              {related.filter((r) => r.slug !== item.slug).slice(0, 3).map((n) => (
                <Link key={n.id} to={`/noticias/${n.slug}`} data-testid={`related-news-${n.slug}`} className="group block">
                  <div className="aspect-[4/3] overflow-hidden mb-4">
                    <img src={n.image_url} alt={n.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="eyebrow mb-1">{formatDate(n.created_at)}</div>
                  <h3 className="font-display text-xl text-forest-900 group-hover:text-copaiba-700 transition-colors">{n.title}</h3>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </article>
  );
}

function ShareBar({ item }) {
  const [copied, setCopied] = useState(false);
  const url = `${window.location.origin}/noticias/${item.slug}`;
  const message = `📰 ${item.title}\n\n${item.excerpt}\n\nLeia mais no site da COOMFLONA:\n${url}`;
  const waLink = `https://wa.me/?text=${encodeURIComponent(message)}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copiado!");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Não foi possível copiar");
    }
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: item.title, text: item.excerpt, url });
      } catch {
        // user cancelled
      }
    } else {
      copyLink();
    }
  };

  return (
    <div className="mt-10 border-t border-cream-400 pt-6" data-testid="news-sharebar">
      <div className="text-[10px] uppercase tracking-[0.18em] text-forest-700 mb-3">Gostou? Compartilhe</div>
      <div className="flex flex-wrap gap-2">
        <a href={waLink} target="_blank" rel="noreferrer" data-testid="share-whatsapp"
           className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#25D366] text-white hover:opacity-90 text-sm">
          <MessageCircle className="w-4 h-4" /> Compartilhar no WhatsApp
        </a>
        <button type="button" onClick={nativeShare} data-testid="share-native"
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-forest-700 text-cream-100 hover:bg-forest-900 text-sm">
          <Share2 className="w-4 h-4" /> Compartilhar
        </button>
        <button type="button" onClick={copyLink} data-testid="share-copy"
          className="inline-flex items-center gap-2 px-4 py-2.5 border border-cream-400 text-forest-900 hover:bg-cream-200 text-sm">
          {copied ? <><Check className="w-4 h-4 text-forest-700" /> Copiado!</> : <><Copy className="w-4 h-4" /> Copiar link</>}
        </button>
      </div>
    </div>
  );
}

function youtubeId(url) {
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([A-Za-z0-9_-]{6,})/);
  return m ? m[1] : null;
}

function NewsMedia({ item, idx }) {
  const base = process.env.REACT_APP_BACKEND_URL;
  const absUrl = item.url && item.url.startsWith("/api/") ? `${base}${item.url}` : item.url;

  if (item.type === "image") {
    return (
      <figure data-testid={`gallery-media-${idx}`} className="bg-white border border-cream-400 p-2">
        <a href={absUrl} target="_blank" rel="noreferrer" className="block">
          <img src={absUrl} alt={item.caption || ""} className="w-full h-64 object-cover" />
        </a>
        {item.caption && <figcaption className="text-xs text-forest-900/70 mt-2 px-1">{item.caption}</figcaption>}
      </figure>
    );
  }
  if (item.type === "video_upload") {
    return (
      <figure data-testid={`gallery-media-${idx}`} className="bg-white border border-cream-400 p-2">
        <video src={absUrl} controls preload="metadata" className="w-full h-64 bg-black object-contain" />
        {item.caption && <figcaption className="text-xs text-forest-900/70 mt-2 px-1">{item.caption}</figcaption>}
      </figure>
    );
  }
  if (item.type === "youtube") {
    const yid = youtubeId(item.url);
    if (yid) {
      return (
        <figure data-testid={`gallery-media-${idx}`} className="bg-white border border-cream-400 p-2">
          <div className="relative pt-[56.25%]">
            <iframe className="absolute inset-0 w-full h-full"
              src={`https://www.youtube.com/embed/${yid}`}
              title="YouTube" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
          </div>
          {item.caption && <figcaption className="text-xs text-forest-900/70 mt-2 px-1">{item.caption}</figcaption>}
        </figure>
      );
    }
  }
  if (item.type === "instagram") {
    return (
      <figure data-testid={`gallery-media-${idx}`} className="bg-white border border-cream-400 p-4 flex flex-col items-center text-center">
        <a href={item.url} target="_blank" rel="noreferrer" className="text-forest-700 hover:text-copaiba-700 underline break-all text-sm">
          📷 Ver no Instagram
        </a>
        <span className="text-xs text-forest-900/60 mt-2 break-all">{item.url}</span>
        {item.caption && <figcaption className="text-xs text-forest-900/70 mt-2">{item.caption}</figcaption>}
      </figure>
    );
  }
  return null;
}
